import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import {
  TbAlertTriangle,
  TbCheck,
  TbKey,
  TbPencil,
  TbSearch,
  TbUserPlus,
  TbArrowLeft,
  TbMailForward,
} from "react-icons/tb";
import { ModalCloseButton } from "../../../Components/Molecules/ValutDialog";
import { PasswordConfirm } from "../../../Components/Molecules/PasswordConfirm";
import { useToast } from "../../../Hooks/toast";
import {
  CARTEIRA_ROLE_PERMISSIONS,
  CARTEIRA_USER_ROLES,
  carteiraUsersErrorMessage,
  createCarteiraUser,
  getVisibleCarteiraPermissionGroups,
  lookupCarteiraPersonByDocument,
  resendCarteiraUserAccess,
  saveCarteiraUser,
} from "../../../Services/carteiraUsersApi";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { isIbProductEnabled } from "../../../Utils/ibProductFeatures";
import { useActiveIbAccount } from "../../../Hooks/useActiveIbAccount";
import { PhonePartsFields } from "../BaasConsole/Entidades/components/baasEntidadeCreateFields";
import {
  buildPhoneDialOptions,
  composeE164FromParts,
  createEmptyPhoneParts,
  formatPhonePartsForDisplay,
  isPhonePartsComplete,
  mapPhonePartsFromPayload,
} from "../../../Services/baasPhoneParts";

const STEP_DADOS = "dados";
const STEP_PERMISSOES = "permissoes";
const STEP_RESULTADO = "resultado";

const DADOS_DOC = "document";
const DADOS_FORM = "form";
const DADOS_ALREADY = "already";

const CREATE_STEPS = [STEP_DADOS, STEP_PERMISSOES, STEP_RESULTADO];

const MODAL_PAPER_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-4xl !max-h-[90vh] outline-none";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const disabledInputClass =
  "h-10 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400 outline-none dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-500";

const roleLabel = {
  admin: "Administrador",
  // Operador = can transact (Pix/Stables/transfers); not a product/menu. Cannot manage Usuários.
  operator: "Operador",
  viewer: "Visualizador",
};

const t = (key, fallback) => {
  const value = intl.get(key);
  return value && value !== key ? value : fallback;
};

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

const formatDocument = (value) => {
  const digits = digitsOnly(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const StepBadge = ({ step, current, label }) => {
  const index = CREATE_STEPS.indexOf(step);
  const currentIndex = CREATE_STEPS.indexOf(current);
  const done = currentIndex > index;
  const active = current === step;

  return (
    <div
      className={`flex items-center gap-2 text-xs font-semibold ${
        active
          ? "text-[#a66f1d]"
          : done
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-gray-400"
      }`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] ${
          active
            ? "bg-[#e7a73f]/20 text-[#a66f1d]"
            : done
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
              : "bg-gray-100 text-gray-400 dark:bg-brand-dark-elevated"
        }`}
      >
        {done ? <TbCheck className="h-3.5 w-3.5" aria-hidden /> : index + 1}
      </span>
      {label}
    </div>
  );
};

const PermissionsPanel = ({
  role,
  onRoleChange,
  permissions,
  onToggle,
  groups = [],
  submitting,
  showStatus = false,
  status,
  onStatusChange,
}) => {
  const shownGroups =
    role === "viewer" ? groups.filter((group) => group.id !== "users") : groups;
  const flatPermissions = shownGroups.flatMap((group) => group.permissions);
  const selectedVisible = permissions.filter((id) =>
    flatPermissions.some((permission) => permission.id === id),
  );
  const viewerLocked = role === "viewer";

  return (
  <div className="space-y-4">
    <div className={`grid gap-4 ${showStatus ? "sm:grid-cols-2" : "sm:grid-cols-1 sm:max-w-sm"}`}>
      <label className="block space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("walletUsers.role", "Perfil")}
        </span>
        <select
          className={inputClass}
          value={role}
          onChange={(event) => onRoleChange(event.target.value)}
          disabled={submitting}
        >
          {CARTEIRA_USER_ROLES.map((item) => (
            <option key={item} value={item}>
              {t(`walletUsers.roles.${item}`, roleLabel[item] ?? item)}
            </option>
          ))}
        </select>
      </label>

      {showStatus ? (
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("walletUsers.status", "Status")}
          </span>
          <select
            className={inputClass}
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            disabled={submitting}
          >
            <option value="active">{t("walletUsers.statusActive", "Ativo")}</option>
            <option value="suspended">{t("walletUsers.statusSuspended", "Suspenso")}</option>
          </select>
        </label>
      ) : null}
    </div>

    {role === "operator" ? (
      <p className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-brand-dark-elevated/40 dark:text-gray-300">
        {t(
          "walletUsers.operatorHint",
          "Operador pode movimentar os produtos atuais (Pix, Stables, transferências). Não gerencia usuários e não é um menu.",
        )}
      </p>
    ) : null}

    {role === "viewer" ? (
      <p className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-brand-dark-elevated/40 dark:text-gray-300">
        {t(
          "walletUsers.viewerHint",
          "Visualizador vê os menus atuais em modo somente leitura. Usuários fica oculto.",
        )}
      </p>
    ) : null}

    {role !== "admin" ? (
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("walletUsers.permissions", "Permissões")}
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          {shownGroups.map((group) => (
            <div
              key={group.id ?? group.group}
              className="rounded-xl border border-gray-100 p-3 dark:border-brand-dark-border"
            >
              <p className="mb-2 text-sm font-semibold text-brand-dark dark:text-gray-100">
                {t(group.groupKey, group.group)}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {group.permissions.map((permission) => (
                  <label
                    key={permission.id}
                    className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={permissions.includes(permission.id)}
                      onChange={() => onToggle(permission.id)}
                      disabled={submitting || viewerLocked}
                    />
                    <span>
                      {permission.name}
                      <span className="ml-1 text-[11px] uppercase text-gray-400">
                        {permission.access}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400">
          {flatPermissions.length} {t("walletUsers.permissionsCatalog", "permissões no catálogo")} ·{" "}
          {selectedVisible.length} {t("walletUsers.permissionsSelected", "selecionadas")}
        </p>
      </div>
    ) : (
      <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
        {t(
          "walletUsers.adminHint",
          "Administrador tem acesso total aos menus atuais da Carteira, inclusive gerenciar usuários.",
        )}
      </p>
    )}
  </div>
  );
};

export const CarteiraUserCreateModal = ({
  open,
  user = null,
  onClose,
  onSaved,
  requireTransactionalPassword = true,
  lookupPersonByDocument: lookupPersonByDocumentProp = null,
  createUser: createUserProp = null,
  saveUser: saveUserProp = null,
  isExitRailVirtualOverride = null,
}) => {
  const { addToast } = useToast();
  const features = useIbProductFeaturesStore((state) => state.features);
  const { isExitRailVirtual: activeAccountExitRailVirtual } = useActiveIbAccount();
  const isExitRailVirtual =
    typeof isExitRailVirtualOverride === "boolean"
      ? isExitRailVirtualOverride
      : activeAccountExitRailVirtual;
  const isEdit = Boolean(user);

  const [step, setStep] = useState(STEP_DADOS);
  const [dadosPhase, setDadosPhase] = useState(DADOS_DOC);
  const [name, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nationalRegistration, setNationalRegistration] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(createEmptyPhoneParts);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [role, setRole] = useState("operator");
  const [status, setStatus] = useState("active");
  const [permissions, setPermissions] = useState(CARTEIRA_ROLE_PERMISSIONS.operator);
  const [submitting, setSubmitting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const [personFound, setPersonFound] = useState(false);
  const [personVerified, setPersonVerified] = useState(false);
  const [hasPlatformAccess, setHasPlatformAccess] = useState(false);
  const [fieldsLocked, setFieldsLocked] = useState(false);

  const [resultUser, setResultUser] = useState(null);
  const [personCreated, setPersonCreated] = useState(false);
  const [inviteKind, setInviteKind] = useState("first_access");
  const [alreadyLinked, setAlreadyLinked] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [resendingAccess, setResendingAccess] = useState(false);
  const phoneDialOptions = useMemo(() => buildPhoneDialOptions(), []);

  const lookupPersonByDocument = lookupPersonByDocumentProp ?? lookupCarteiraPersonByDocument;
  const createUser = createUserProp ?? createCarteiraUser;
  const saveUser = saveUserProp ?? saveCarteiraUser;

  const resetCreateForm = () => {
    setStep(STEP_DADOS);
    setDadosPhase(DADOS_DOC);
    setName("");
    setMiddleName("");
    setLastName("");
    setNationalRegistration("");
    setEmail("");
    setPhone(createEmptyPhoneParts());
    setDateOfBirth("");
    setRole("operator");
    setStatus("active");
    setPermissions(CARTEIRA_ROLE_PERMISSIONS.operator);
    setPersonFound(false);
    setPersonVerified(false);
    setHasPlatformAccess(false);
    setFieldsLocked(false);
    setResultUser(null);
    setPersonCreated(false);
    setInviteKind("first_access");
    setAlreadyLinked(null);
    setPasswordOpen(false);
    setPasswordError(null);
    setResendingAccess(false);
  };

  const applyPersonLookup = (person, { verified, platformAccess }) => {
    setName(person?.name ?? "");
    setMiddleName(person?.middleName ?? "");
    setLastName(person?.lastName ?? "");
    setEmail(person?.email ?? "");
    setDateOfBirth(person?.dateOfBirth ?? "");
    setPhone(mapPhonePartsFromPayload({ phone_number: person?.phone }));
    if (person?.nationalRegistration) {
      setNationalRegistration(formatDocument(person.nationalRegistration));
    }
    setPersonFound(true);
    setPersonVerified(Boolean(verified));
    setHasPlatformAccess(Boolean(platformAccess));
    setFieldsLocked(true);
  };

  useEffect(() => {
    if (!open) return;

    if (user) {
      setStep(STEP_DADOS);
      setDadosPhase(DADOS_FORM);
      setEmail(user.email ?? "");
      setName(user.name ?? "");
      setMiddleName("");
      setLastName("");
      setNationalRegistration(user.nationalRegistration ?? "");
      setPhone(createEmptyPhoneParts());
      setDateOfBirth("");
      setRole(user.role ?? "operator");
      setStatus(user.status === "suspended" ? "suspended" : "active");
      setPermissions(
        Array.isArray(user.permissions) && user.permissions.length > 0
          ? user.permissions
          : CARTEIRA_ROLE_PERMISSIONS[user.role] ?? CARTEIRA_ROLE_PERMISSIONS.operator,
      );
      setResultUser(null);
      setPersonCreated(false);
      setInviteKind("first_access");
      setAlreadyLinked(null);
      setPersonFound(false);
      setPersonVerified(false);
      setHasPlatformAccess(false);
      setFieldsLocked(false);
      setPasswordOpen(false);
      setPasswordError(null);
      setResendingAccess(false);
      return;
    }

    resetCreateForm();
  }, [open, user]);

  const visiblePermissionGroups = useMemo(
    () =>
      getVisibleCarteiraPermissionGroups({
        features,
        isExitRailVirtual,
        isFeatureEnabled: (code) => isIbProductEnabled(features, code),
      }),
    [features, isExitRailVirtual],
  );

  const togglePermission = (id) => {
    setPermissions((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    setPermissions(CARTEIRA_ROLE_PERMISSIONS[nextRole] ?? []);
  };

  const validateDocument = () => {
    const docDigits = digitsOnly(nationalRegistration);
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      addToast({
        type: "error",
        title: t(
          "walletUsers.errors.invalidDocument",
          "Informe um CPF (11) ou CNPJ (14 dígitos).",
        ),
      });
      return false;
    }
    return true;
  };

  const validateDados = () => {
    if (!name.trim()) {
      addToast({
        type: "error",
        title: t("walletUsers.errors.nameRequired", "Informe o nome da pessoa."),
      });
      return false;
    }
    if (!email.trim()) {
      addToast({
        type: "error",
        title: t("walletUsers.errors.emailRequired", "Informe o e-mail."),
      });
      return false;
    }
    if (!validateDocument()) return false;
    if (!isPhonePartsComplete(phone)) {
      addToast({
        type: "error",
        title: t(
          "walletUsers.errors.phoneRequired",
          "Informe o telefone (DDI + DDD + número).",
        ),
      });
      return false;
    }
    return true;
  };

  const validatePermissoes = () => {
    if (role !== "admin" && permissions.length === 0) {
      addToast({
        type: "error",
        title: t(
          "walletUsers.errors.permissionsRequired",
          "Selecione ao menos uma permissão ou o perfil Administrador.",
        ),
      });
      return false;
    }
    return true;
  };

  const handleLookupDocument = async (event) => {
    event?.preventDefault?.();
    if (lookingUp || submitting) return;
    if (!validateDocument()) return;

    setLookingUp(true);
    try {
      const result = await lookupPersonByDocument(nationalRegistration);

      if (result.alreadyMember) {
        applyPersonLookup(result.person, {
          verified: result.verified,
          platformAccess: result.hasPlatformAccess,
        });
        setAlreadyLinked({
          userId: result.userId,
          personId: result.person?.personId ?? null,
          accessStatus: result.accessStatus,
          hasPlatformAccess: result.hasPlatformAccess,
        });
        setDadosPhase(DADOS_ALREADY);
        addToast({
          type: "info",
          title: t(
            "walletUsers.lookup.alreadyLinked",
            "Esta pessoa já é usuária desta Carteira.",
          ),
        });
        return;
      }

      if (!result.found) {
        setPersonFound(false);
        setPersonVerified(false);
        setHasPlatformAccess(false);
        setAlreadyLinked(null);
        setFieldsLocked(false);
        setName("");
        setMiddleName("");
        setLastName("");
        setEmail("");
        setPhone(createEmptyPhoneParts());
        setDateOfBirth("");
        setDadosPhase(DADOS_FORM);
        addToast({
          type: "info",
          title: t(
            "walletUsers.lookup.notFound",
            "Nenhuma pessoa encontrada. Preencha os dados para cadastrar.",
          ),
        });
        return;
      }

      applyPersonLookup(result.person, {
        verified: result.verified,
        platformAccess: result.hasPlatformAccess,
      });
      setAlreadyLinked(null);

      if (result.hasPlatformAccess) {
        setStep(STEP_PERMISSOES);
        addToast({
          type: "success",
          title: t(
            "walletUsers.lookup.platformAccessFound",
            "Pessoa já tem conta Valut. Defina as permissões — enviaremos um convite de acesso.",
          ),
        });
        return;
      }

      if (result.verified) {
        setStep(STEP_PERMISSOES);
        addToast({
          type: "success",
          title: t(
            "walletUsers.lookup.verifiedFound",
            "Pessoa já verificada. Defina as permissões.",
          ),
        });
        return;
      }

      setDadosPhase(DADOS_FORM);
      addToast({
        type: "success",
        title: t(
          "walletUsers.lookup.found",
          "Pessoa encontrada. Confira os dados e continue.",
        ),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: carteiraUsersErrorMessage(
          error,
          t("walletUsers.errors.lookupFailed", "Não foi possível buscar o documento."),
        ),
      });
    } finally {
      setLookingUp(false);
    }
  };

  const handleChangeDocument = () => {
    setDadosPhase(DADOS_DOC);
    setPersonFound(false);
    setPersonVerified(false);
    setHasPlatformAccess(false);
    setAlreadyLinked(null);
    setFieldsLocked(false);
    setName("");
    setMiddleName("");
    setLastName("");
    setEmail("");
    setPhone(createEmptyPhoneParts());
    setDateOfBirth("");
  };

  const handleResendAccessEmail = async (target = alreadyLinked || user) => {
    if (resendingAccess || submitting) return;
    const targetUser = {
      id: target?.userId ?? target?.id,
      personId: target?.personId ?? alreadyLinked?.personId,
    };
    if (!targetUser.id && !targetUser.personId) {
      addToast({
        type: "error",
        title: t(
          "walletUsers.errors.resendAccessFailed",
          "Não foi possível reenviar o e-mail de acesso.",
        ),
      });
      return;
    }

    const confirmed = window.confirm(
      t(
        "walletUsers.access.resendConfirm",
        "Reenviar o e-mail de acesso para {email}?",
      ).replace("{email}", email || ""),
    );
    if (!confirmed) return;

    setResendingAccess(true);
    try {
      const result = await resendCarteiraUserAccess(targetUser);
      const masked = String(result?.email || "").trim();
      addToast({
        type: "success",
        title: masked
          ? t(
              "walletUsers.access.resendSuccessTo",
              "E-mail de acesso reenviado para {email}.",
            ).replace("{email}", masked)
          : t("walletUsers.access.resendSuccess", "E-mail de acesso reenviado."),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: carteiraUsersErrorMessage(
          error,
          t(
            "walletUsers.errors.resendAccessFailed",
            "Não foi possível reenviar o e-mail de acesso.",
          ),
        ),
      });
    } finally {
      setResendingAccess(false);
    }
  };

  const handleContinueFromDados = (event) => {
    event.preventDefault();
    if (submitting) return;

    if (isEdit) return;

    if (!validateDados()) return;
    setStep(STEP_PERMISSOES);
  };

  const handleSubmitEdit = async (event) => {
    event.preventDefault();
    if (submitting || !isEdit) return;

    if (!validatePermissoes()) return;

    setSubmitting(true);
    try {
      await saveUser(user, {
        role,
        permissions: role === "admin" ? ["admin"] : permissions,
        status,
      });
      addToast({
        type: "success",
        title: t("walletUsers.toasts.updated", "Usuário atualizado."),
      });
      onSaved?.();
      onClose?.();
    } catch (error) {
      addToast({
        type: "error",
        title: carteiraUsersErrorMessage(
          error,
          t("walletUsers.errors.saveFailed", "Não foi possível salvar o usuário."),
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closePasswordModal = () => {
    if (submitting) return;
    setPasswordOpen(false);
    setPasswordError(null);
  };

  const submitCreateUser = async (passwordCombination = null) => {
    if (submitting) return;
    setSubmitting(true);
    setPasswordError(null);
    try {
      const result = await createUser({
        email: email.trim(),
        name: name.trim(),
        middleName: middleName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        nationalRegistration: digitsOnly(nationalRegistration),
        phone: composeE164FromParts(phone).replace(/\D/g, "") || undefined,
        phoneCountryDialCode: digitsOnly(phone?.countryDialCode) || undefined,
        phoneAreaCode: digitsOnly(phone?.areaCode) || undefined,
        phoneLocalNumber: digitsOnly(phone?.number) || undefined,
        dateOfBirth: dateOfBirth || undefined,
        role,
        permissions: role === "admin" ? ["admin"] : permissions,
        status: "active",
        passwordCombination,
      });

      setPasswordOpen(false);
      setResultUser(result.user);
      setPersonCreated(Boolean(result.personCreated));
      setInviteKind(result.inviteKind === "wallet_access" ? "wallet_access" : "first_access");
      setStep(STEP_RESULTADO);
      onSaved?.();
      const grantedExisting = result.inviteKind === "wallet_access";
      addToast({
        type: "success",
        title: result.personCreated
          ? t(
              "walletUsers.toasts.personCreatedActive",
              "Pessoa cadastrada e acesso liberado.",
            )
          : grantedExisting
            ? t(
                "walletUsers.toasts.walletAccessInvite",
                "Convite de acesso enviado. A pessoa já tem conta Valut.",
              )
            : t(
                "walletUsers.toasts.inviteCreatedActive",
                "Usuário adicionado e acesso liberado.",
              ),
      });
    } catch (error) {
      const code = String(error?.response?.data?.error ?? "").trim();
      if (
        code === "invalid_password"
        || code === "invalid_combination"
        || code === "password_not_set"
        || code === "password_corrupt"
      ) {
        setPasswordError({
          title: t("passwordConfirm.warning", "Aviso"),
          description:
            code === "password_not_set"
              ? t(
                  "walletUsers.errors.passwordNotSet",
                  "Cadastre sua senha transacional em Meu perfil para adicionar usuários.",
                )
              : t(
                  "walletUsers.errors.passwordInvalid",
                  "Senha transacional inválida.",
                ),
        });
        return;
      }
      setPasswordOpen(false);
      addToast({
        type: "error",
        title: carteiraUsersErrorMessage(
          error,
          t("walletUsers.errors.createFailed", "Não foi possível adicionar o usuário."),
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueFromPermissoes = (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validateDados()) {
      setStep(STEP_DADOS);
      setDadosPhase(DADOS_FORM);
      return;
    }
    if (!validatePermissoes()) return;

    if (!requireTransactionalPassword) {
      submitCreateUser();
      return;
    }

    setPasswordError(null);
    setPasswordOpen(true);
  };

  const handleSubmitPassword = async (passwordCombination) => {
    await submitCreateUser(passwordCombination);
  };

  const stepTitle = {
    [STEP_DADOS]:
      dadosPhase === DADOS_DOC
        ? t("walletUsers.steps.documentTitle", "Documento")
        : dadosPhase === DADOS_ALREADY
          ? t("walletUsers.steps.alreadyTitle", "Já vinculada")
          : t("walletUsers.steps.dadosTitle", "Dados da pessoa"),
    [STEP_PERMISSOES]: t("walletUsers.steps.permissoesTitle", "Permissões"),
    [STEP_RESULTADO]: t("walletUsers.steps.resultadoTitle", "Usuário adicionado"),
  };

  const stepSubtitle = {
    [STEP_DADOS]:
      dadosPhase === DADOS_DOC
        ? t(
            "walletUsers.steps.documentSubtitle",
            "Informe o CPF ou CNPJ. Se a pessoa já existir, completamos os dados automaticamente.",
          )
        : dadosPhase === DADOS_ALREADY
          ? t(
              "walletUsers.steps.alreadySubtitle",
              "Esta pessoa já tem acesso a esta Carteira. Não é necessário cadastrar de novo.",
            )
          : personFound
            ? hasPlatformAccess
              ? t(
                  "walletUsers.steps.dadosPlatformSubtitle",
                  "Pessoa com conta Valut. Confira os dados — vamos enviar um convite de acesso a esta Carteira.",
                )
              : t(
                  "walletUsers.steps.dadosFoundSubtitle",
                  "Pessoa encontrada no banco. Confira os dados antes de definir as permissões.",
                )
            : t(
                "walletUsers.steps.dadosSubtitle",
                "Informe os dados da pessoa (AccountPerson) que terá acesso à Carteira.",
              ),
    [STEP_PERMISSOES]: hasPlatformAccess
      ? t(
          "walletUsers.steps.permissoesPlatformSubtitle",
          "Escolha o perfil. A pessoa já tem conta Valut — enviaremos um convite de acesso, sem criar cadastro de novo.",
        )
      : t(
          "walletUsers.steps.permissoesSubtitle",
          "Escolha o perfil e as permissões dos menus atuais da Carteira.",
        ),
    [STEP_RESULTADO]:
      inviteKind === "wallet_access"
        ? t(
            "walletUsers.steps.resultadoSubtitleGrant",
            "Enviamos um convite de acesso a esta Carteira.",
          )
        : t(
            "walletUsers.steps.resultadoSubtitleNoVerify",
            "O acesso à Carteira foi liberado.",
          ),
  };

  const headerIcon = (() => {
    if (isEdit) return <TbPencil className="h-5 w-5 text-[#a66f1d]" aria-hidden />;
    if (step === STEP_PERMISSOES) return <TbKey className="h-5 w-5 text-[#a66f1d]" aria-hidden />;
    if (step === STEP_RESULTADO) return <TbCheck className="h-5 w-5 text-[#a66f1d]" aria-hidden />;
    if (dadosPhase === DADOS_DOC) return <TbSearch className="h-5 w-5 text-[#a66f1d]" aria-hidden />;
    return <TbUserPlus className="h-5 w-5 text-[#a66f1d]" aria-hidden />;
  })();

  const busy = submitting || lookingUp || resendingAccess;
  const resultDisplayName =
    resultUser?.name ||
    [name, middleName, lastName].map((part) => part.trim()).filter(Boolean).join(" ");

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ className: MODAL_PAPER_CLASS }}
    >
      <div className="relative flex shrink-0 items-start gap-3 border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e7a73f]/15">
          {headerIcon}
        </div>
        <div className="min-w-0 flex-1 pr-8">
          <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
            {isEdit
              ? t("walletUsers.editTitle", "Editar usuário")
              : stepTitle[step] ?? t("walletUsers.createTitle", "Adicionar usuário")}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {isEdit
              ? t(
                  "walletUsers.editSubtitle",
                  "Permissões da Carteira (visualizar vs escrever).",
                )
              : stepSubtitle[step]}
          </p>
          {!isEdit ? (
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              <StepBadge
                step={STEP_DADOS}
                current={step}
                label={t("walletUsers.steps.dados", "Dados")}
              />
              <StepBadge
                step={STEP_PERMISSOES}
                current={step}
                label={t("walletUsers.steps.permissoes", "Permissões")}
              />
              <StepBadge
                step={STEP_RESULTADO}
                current={step}
                label={t("walletUsers.steps.resultado", "Conclusão")}
              />
            </div>
          ) : null}
        </div>
        {!busy && !passwordOpen ? (
          <ModalCloseButton onClick={onClose} className="absolute right-3 top-3" />
        ) : null}
      </div>

      {!isEdit && requireTransactionalPassword && passwordOpen ? (
        <PasswordConfirm
          variant="embedded"
          open
          submitPassword={handleSubmitPassword}
          onBack={closePasswordModal}
          onClose={closePasswordModal}
          error={passwordError}
          loading={submitting}
          onDismissError={() => setPasswordError(null)}
          title={t(
            "walletUsers.steps.passwordTitle",
            "Confirme com a senha transacional",
          )}
          subtitle={t(
            "walletUsers.steps.passwordSubtitle",
            "Informe sua senha transacional de 4 dígitos para adicionar este usuário.",
          )}
        />
      ) : null}

      {/* Edit: single form with email + permissions */}
      {isEdit ? (
        <form onSubmit={handleSubmitEdit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("walletUsers.fields.email", "E-mail")}
              </span>
              <input type="email" className={disabledInputClass} value={email} disabled />
            </label>
            {String(email || "").includes("@") ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t(
                    "walletUsers.access.resendHint",
                    "Reenvia o e-mail de convite de acesso (sem criar cadastro de novo).",
                  )}
                </p>
                <button
                  type="button"
                  className="btn-outline h-9"
                  disabled={submitting || resendingAccess}
                  onClick={() => {
                    void handleResendAccessEmail(user);
                  }}
                >
                  <TbMailForward className="h-4 w-4" aria-hidden />
                  {resendingAccess
                    ? t("walletUsers.actions.resendingAccess", "Reenviando…")
                    : t("walletUsers.actions.resendAccess", "Reenviar acesso")}
                </button>
              </div>
            ) : null}
            <PermissionsPanel
              role={role}
              onRoleChange={handleRoleChange}
              permissions={permissions}
              onToggle={togglePermission}
              groups={visiblePermissionGroups}
              submitting={submitting}
              showStatus
              status={status}
              onStatusChange={setStatus}
            />
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {t("walletUsers.actions.save", "Salvar")}
            </button>
          </div>
        </form>
      ) : null}

      {/* Step 1a — Documento primeiro */}
      {!isEdit && !passwordOpen && step === STEP_DADOS && dadosPhase === DADOS_DOC ? (
        <form onSubmit={handleLookupDocument} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <label className="block max-w-md space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("walletUsers.fields.document", "CPF / CNPJ")} *
              </span>
              <input
                className={inputClass}
                value={nationalRegistration}
                onChange={(event) =>
                  setNationalRegistration(formatDocument(event.target.value))
                }
                disabled={busy}
                required
                autoFocus
                inputMode="numeric"
                placeholder="000.000.000-00"
              />
            </label>

            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              {t(
                "walletUsers.lookup.hint",
                "Se a pessoa já tiver conta Valut, enviamos um convite de acesso a esta Carteira. Se ainda não existir, enviamos o convite para criar o primeiro acesso.",
              )}
            </p>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button type="submit" className="btn-primary" disabled={busy}>
              {lookingUp
                ? t("walletUsers.actions.searching", "Buscando…")
                : t("walletUsers.actions.search", "Buscar")}
            </button>
          </div>
        </form>
      ) : null}

      {/* Step 1a2 — Já vinculada a esta Carteira */}
      {!isEdit && !passwordOpen && step === STEP_DADOS && dadosPhase === DADOS_ALREADY ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              {t(
                "walletUsers.lookup.alreadyLinkedBanner",
                "Esta pessoa já é usuária ativa desta Carteira. Não enviaremos um convite para criar conta de novo.",
              )}
            </p>
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3 text-sm dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
              <p className="font-medium text-brand-dark dark:text-gray-100">
                {[name, middleName, lastName].map((part) => part.trim()).filter(Boolean).join(" ")}
              </p>
              <p className="text-gray-600 dark:text-gray-400">{email.trim()}</p>
              <p className="mt-1 text-xs text-gray-500">
                {formatDocument(nationalRegistration)}
              </p>
              {hasPlatformAccess ? (
                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  {t(
                    "walletUsers.lookup.platformAccessBadge",
                    "Já possui conta Valut (login).",
                  )}
                </p>
              ) : null}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t(
                "walletUsers.lookup.alreadyLinkedHint",
                "Para atualizar permissões, edite o usuário na lista. Se precisar, reenvie só o e-mail de acesso.",
              )}
            </p>
          </div>
          <div className="flex shrink-0 justify-between gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button
              type="button"
              className="btn-back"
              onClick={handleChangeDocument}
              disabled={busy}
            >
              <TbArrowLeft className="h-4 w-4" />
              {t("walletUsers.actions.backToDocument", "Voltar ao documento")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || (!alreadyLinked?.userId && !alreadyLinked?.personId)}
              onClick={() => {
                void handleResendAccessEmail(alreadyLinked);
              }}
            >
              <TbMailForward className="h-4 w-4" aria-hidden />
              {resendingAccess
                ? t("walletUsers.actions.resendingAccess", "Reenviando…")
                : t("walletUsers.actions.resendAccess", "Reenviar acesso")}
            </button>
          </div>
        </div>
      ) : null}

      {/* Step 1b — Dados (nova pessoa ou existente não verificada) */}
      {!isEdit && !passwordOpen && step === STEP_DADOS && dadosPhase === DADOS_FORM ? (
        <form onSubmit={handleContinueFromDados} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="block min-w-[220px] flex-1 space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.document", "CPF / CNPJ")} *
                </span>
                <input
                  className={fieldsLocked ? disabledInputClass : inputClass}
                  value={nationalRegistration}
                  onChange={(event) =>
                    setNationalRegistration(formatDocument(event.target.value))
                  }
                  disabled={busy || fieldsLocked}
                  required
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                />
              </label>
              {fieldsLocked ? (
                <button
                  type="button"
                  className="btn-outline h-10"
                  onClick={handleChangeDocument}
                  disabled={busy}
                >
                  {t("walletUsers.actions.changeDocument", "Alterar documento")}
                </button>
              ) : null}
            </div>

            {personFound ? (
              <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                {hasPlatformAccess
                  ? t(
                      "walletUsers.lookup.platformAccessBanner",
                      "Pessoa já tem conta Valut — vamos enviar um convite de acesso a esta Carteira, sem criar cadastro de novo.",
                    )
                  : t(
                      "walletUsers.lookup.foundBanner",
                      "Pessoa já cadastrada — dados preenchidos automaticamente.",
                    )}
              </p>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5 sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.name", "Nome")} *
                </span>
                <input
                  className={fieldsLocked && name ? disabledInputClass : inputClass}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={busy || (fieldsLocked && Boolean(name))}
                  required
                  placeholder="Nome"
                  autoComplete="given-name"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.middleName", "Nome do meio")}
                </span>
                <input
                  className={fieldsLocked && middleName ? disabledInputClass : inputClass}
                  value={middleName}
                  onChange={(event) => setMiddleName(event.target.value)}
                  disabled={busy || (fieldsLocked && Boolean(middleName))}
                  placeholder={t("walletUsers.fields.optional", "Opcional")}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.lastName", "Sobrenome")}
                </span>
                <input
                  className={fieldsLocked && lastName ? disabledInputClass : inputClass}
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  disabled={busy || (fieldsLocked && Boolean(lastName))}
                  placeholder={t("walletUsers.fields.optional", "Opcional")}
                  autoComplete="family-name"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.birthDate", "Data de nascimento")}
                </span>
                <input
                  type="date"
                  className={fieldsLocked && dateOfBirth ? disabledInputClass : inputClass}
                  value={dateOfBirth}
                  onChange={(event) => setDateOfBirth(event.target.value)}
                  disabled={busy || (fieldsLocked && Boolean(dateOfBirth))}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("walletUsers.fields.email", "E-mail")} *
                </span>
                <input
                  type="email"
                  className={fieldsLocked && email ? disabledInputClass : inputClass}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={busy || (fieldsLocked && Boolean(email))}
                  required
                  placeholder="pessoa@empresa.com"
                />
              </label>
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("walletUsers.fields.phone", "Telefone")} *
              </span>
              <PhonePartsFields
                value={phone}
                onChange={(next) => {
                  setPhone((current) => ({ ...current, ...next }));
                }}
                dialOptions={phoneDialOptions}
                required
                disabled={busy}
              />
              <span className="mt-1 block text-xs text-gray-400">
                {t(
                  "walletUsers.fields.phoneHint",
                  "Obrigatório. Gravado em AccountPersonPhone (DDI, DDD e número).",
                )}
              </span>
            </div>

            <div
              role="status"
              className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-950/30"
            >
              <TbAlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
                aria-hidden
              />
              <p className="min-w-0 flex-1 text-sm leading-relaxed text-red-800 dark:text-red-200">
                {t(
                  "walletUsers.steps.dadosNextHintNoVerify",
                  "No próximo passo você define o perfil e as permissões. O acesso é liberado ao concluir.",
                )}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 justify-between gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button
              type="button"
              className="btn-back"
              onClick={handleChangeDocument}
              disabled={busy}
            >
              <TbArrowLeft className="h-4 w-4" />
              {t("walletUsers.actions.backToDocument", "Voltar ao documento")}
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {t("walletUsers.actions.continuePermissions", "Continuar para permissões")}
            </button>
          </div>
        </form>
      ) : null}

      {/* Step 2 — Permissões */}
      {!isEdit && !passwordOpen && step === STEP_PERMISSOES ? (
        <form onSubmit={handleContinueFromPermissoes} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3 text-sm dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
              <p className="font-medium text-brand-dark dark:text-gray-100">
                {[name, middleName, lastName].map((part) => part.trim()).filter(Boolean).join(" ")}
              </p>
              <p className="text-gray-600 dark:text-gray-400">{email.trim()}</p>
              <p className="mt-1 text-xs text-gray-500">
                {formatDocument(nationalRegistration)}
                {isPhonePartsComplete(phone)
                  ? ` · ${formatPhonePartsForDisplay(phone)}`
                  : ""}
              </p>
              {hasPlatformAccess ? (
                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  {t(
                    "walletUsers.lookup.platformAccessBadge",
                    "Já possui conta Valut (login).",
                  )}
                </p>
              ) : personVerified ? (
                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  {t(
                    "walletUsers.lookup.verifiedBadgeNoVerify",
                    "Pessoa já verificada no cadastro.",
                  )}
                </p>
              ) : null}
            </div>

            <PermissionsPanel
              role={role}
              onRoleChange={handleRoleChange}
              permissions={permissions}
              onToggle={togglePermission}
              groups={visiblePermissionGroups}
              submitting={submitting}
            />
          </div>

          <div className="flex shrink-0 justify-between gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button
              type="button"
              className="btn-back"
              onClick={() => {
                setStep(STEP_DADOS);
                setDadosPhase(personVerified || personFound ? DADOS_FORM : DADOS_DOC);
              }}
              disabled={submitting}
            >
              <TbArrowLeft className="h-4 w-4" />
              {t("walletUsers.actions.back", "Voltar")}
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {t("walletUsers.actions.finishAdd", "Adicionar usuário")}
            </button>
          </div>
        </form>
      ) : null}

      {/* Step 3 — Conclusão */}
      {!isEdit && !passwordOpen && step === STEP_RESULTADO ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/50">
              <TbCheck className="h-7 w-7 text-emerald-600 dark:text-emerald-300" aria-hidden />
            </div>
            <div>
              <p className="text-lg font-semibold text-brand-dark dark:text-gray-100">
                {resultDisplayName}{" "}
                {t("walletUsers.result.activeSuffix", "está ativo")}
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {inviteKind === "wallet_access"
                  ? t(
                      "walletUsers.result.doneWalletAccess",
                      "Enviamos um convite de acesso. A pessoa pode entrar com a conta Valut que já possui.",
                    )
                  : personCreated
                    ? t(
                        "walletUsers.result.doneNewPerson",
                        'Acesso liberado. Se for o primeiro acesso, a pessoa deve definir senha em "Meu primeiro acesso".',
                      )
                    : t(
                        "walletUsers.result.doneExisting",
                        "Acesso liberado para esta Carteira.",
                      )}
              </p>
            </div>
            <div className="mx-auto max-w-sm rounded-xl border border-gray-100 px-4 py-3 text-left text-sm dark:border-brand-dark-border">
              <p>
                <span className="text-gray-400">{t("walletUsers.fields.email", "E-mail")}:</span>{" "}
                {resultUser?.email || email}
              </p>
              <p className="mt-1">
                <span className="text-gray-400">{t("walletUsers.role", "Perfil")}:</span>{" "}
                {roleLabel[resultUser?.role ?? role] ?? resultUser?.role ?? role}
              </p>
              <p className="mt-1">
                <span className="text-gray-400">{t("walletUsers.status", "Status")}:</span>{" "}
                {t("walletUsers.statusActive", "Ativo")}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <button type="button" className="btn-primary" onClick={onClose}>
              {t("walletUsers.actions.finish", "Concluir")}
            </button>
          </div>
        </div>
      ) : null}

    </Dialog>
  );
};
